import { Box } from "../../box";
import { cloneElement, forwardRef, useCallback, useMemo } from "react";
import { isNil, isString, mergeProps, useAutoFocusChild, useEventCallback, useFocusManager, useFocusScope, useId, useMergedRefs, useSlots } from "../../shared";
import { Text } from "../../typography";
import { useOverlayFocusRing, useTrapFocus } from "../../overlay";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "section";
export function InnerPopover({
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  as = DefaultElement,
  children,
  focus,
  forwardedRef,
  id,
  ...rest
}) {
  var _heading$props, _footer$props, _footer$props2;

  const [focusScope, setFocusRef] = useFocusScope();
  const popoverRef = useMergedRefs(forwardedRef, setFocusRef);
  const focusManager = useFocusManager(focusScope);
  useTrapFocus(focusManager);
  useAutoFocusChild(focusManager, {
    canFocus: useCallback(element => {
      // Do not autofocus the popover itself.
      if (element === popoverRef.current) {
        return false;
      } // Do not autofocus a link.


      if ((element === null || element === void 0 ? void 0 : element.tagName) === "A") {
        return false;
      }

      return true;
    }, [popoverRef]),
    onNotFound: useEventCallback(() => {
      var _popoverRef$current;

      (_popoverRef$current = popoverRef.current) === null || _popoverRef$current === void 0 ? void 0 : _popoverRef$current.focus();
    })
  });
  const focusRingProps = useOverlayFocusRing({
    focus
  });
  const popoverId = useId(id, "o-ui-popover");
  const {
    button,
    "button-group": buttonGroup,
    content,
    footer,
    heading
  } = useSlots(children, useMemo(() => ({
    _: {
      required: ["heading", "content"]
    },
    button: {
      className: "o-ui-popover-button",
      size: "sm"
    },
    "button-group": {
      className: "o-ui-popover-button-group",
      size: "sm"
    },
    content: {
      as: Text,
      className: "o-ui-popover-content"
    },
    footer: {
      as: "footer",
      className: "o-ui-popover-footer"
    },
    heading: {
      as: "h3",
      id: `${popoverId}-heading`,
      size: "sm"
    }
  }), [popoverId]));
  const headingId = heading === null || heading === void 0 ? void 0 : (_heading$props = heading.props) === null || _heading$props === void 0 ? void 0 : _heading$props.id;
  const footerMarkup = isString(footer === null || footer === void 0 ? void 0 : (_footer$props = footer.props) === null || _footer$props === void 0 ? void 0 : _footer$props.children) ? /*#__PURE__*/cloneElement(footer, {
    children: /*#__PURE__*/_jsx(Text, {
      children: footer === null || footer === void 0 ? void 0 : (_footer$props2 = footer.props) === null || _footer$props2 === void 0 ? void 0 : _footer$props2.children
    })
  }) : footer;

  const headerSectionMarkup = heading && /*#__PURE__*/_jsx("header", {
    className: "o-ui-popover-header-section",
    children: heading
  });

  const footerSectionMarkup = (footer || button || buttonGroup) && /*#__PURE__*/_jsxs("footer", {
    className: "o-ui-popover-footer-section",
    children: [footerMarkup, button, buttonGroup]
  });

  return /*#__PURE__*/_jsxs(Box, { ...mergeProps(rest, {
      "aria-label": ariaLabel,
      "aria-labelledby": isNil(ariaLabel) ? ariaLabelledBy !== null && ariaLabelledBy !== void 0 ? ariaLabelledBy : headingId : undefined,
      as,
      className: "o-ui-popover",
      id,
      ref: popoverRef,
      role: "dialog",
      tabIndex: -1
    }, focusRingProps),
    children: [headerSectionMarkup, content, footerSectionMarkup]
  });
}
InnerPopover.defaultElement = DefaultElement;
export const Popover = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerPopover, { ...props,
  forwardedRef: ref
}));